import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// One place for the "how do I format a subscription's current price?" logic
// so this table stays readable. Shows the effective per-unit price + the
// total for the cycle, and expands to a tier breakdown when the sub spans
// multiple bands.
function formatUnit(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return "-";
  const n = Number(amount);
  const sym = currency === "inr" ? "₹" : currency === "usd" ? "$" : "";
  // No decimals for INR (rupees don't need paise in this UI), 2 for USD.
  const digits = currency === "inr" ? 0 : 2;
  return `${sym}${n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function formatInterval(n_interval, interval) {
  const n = Number(n_interval) || 1;
  const label = interval || "cycle";
  return `${n} ${label}${n > 1 ? "s" : ""}`;
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Try amount_default first (server-side authoritative total), fall back to
// amount × noOfLicences when amount_default is missing on legacy rows.
function totalAmount(sub) {
  if (sub.amount_default != null && !Number.isNaN(Number(sub.amount_default))) {
    return Number(sub.amount_default);
  }
  const per = Number(sub.amount) || 0;
  const q = Number(sub.noOfLicences) || 0;
  return per * q;
}

function unitBreakdown(sub) {
  const rows = Array.isArray(sub.unit_ids) ? sub.unit_ids : [];
  if (!rows.length) return null;
  // Sort by start_range so tiers read low-to-high.
  const sorted = [...rows].sort(
    (a, b) => (Number(a.start_range) || 0) - (Number(b.start_range) || 0),
  );
  return sorted.map((u, i) => {
    const start = u.start_range ?? 0;
    const end = u.end_range == null || u.end_range === "" ? "∞" : u.end_range;
    const price = formatUnit(u.marketplace_price, sub.currency);
    const qty = u.quantity != null ? ` × ${u.quantity}` : "";
    return (
      <div key={i} style={{ fontSize: "0.75rem", color: "#4b5563" }}>
        {start}–{end}: {price}/unit{qty}
      </div>
    );
  });
}

const statusColor = (s) => {
  if (s === "active") return { bg: "#d1fae5", fg: "#065f46" };
  if (s === "overdue") return { bg: "#fee2e2", fg: "#991b1b" };
  if (s === "cancel_next") return { bg: "#fef3c7", fg: "#92400e" };
  if (s === "cancelled") return { bg: "#e5e7eb", fg: "#4b5563" };
  return { bg: "#e0e7ff", fg: "#3730a3" };
};

const ActiveSubscriptionsBadge = ({ priceId, count }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !priceId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${process.env.REACT_APP_SERVER_URL}/admin/get_plan_subscriptions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ price_id: priceId }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json?.status === 200 && Array.isArray(json.data)) {
          setSubs(json.data);
        } else {
          setError(json?.msg || "Failed to load subscriptions");
        }
      })
      .catch((e) => !cancelled && setError(String(e?.message || e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, priceId]);

  if (!priceId) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          border: "1px solid #92400e",
          background: "transparent",
          color: "#92400e",
          borderRadius: 8,
          padding: "4px 10px",
          fontSize: "0.75rem",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        See all subscriptions{count ? ` (${count})` : ""}
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        // The pricing editor lives inside the "Edit listing" drawer which
        // sets a very high z-index; MUI's default (1300) loses to it and
        // the dialog opens under the drawer. Sit above the drawer stack.
        sx={{ zIndex: 15000 }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          <span>Subscriptions on this plan</span>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && error && (
            <Box sx={{ color: "#991b1b", py: 2 }}>{error}</Box>
          )}
          {!loading && !error && subs.length === 0 && (
            <Box sx={{ color: "#6b7280", py: 3, textAlign: "center" }}>
              No subscriptions currently attached to this plan.
            </Box>
          )}
          {!loading && !error && subs.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Licenses</TableCell>
                  <TableCell align="right">Unit price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Interval</TableCell>
                  <TableCell>Renewal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subs.map((s) => {
                  const sc = statusColor(s.status);
                  const breakdown = unitBreakdown(s);
                  const perUnit =
                    s.amount != null
                      ? formatUnit(s.amount, s.currency)
                      : "-";
                  return (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>
                          {s.company || s.workspace_name || "—"}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                          {s.owner_email || ""}
                        </div>
                        {s.marketplaceName && (
                          <div style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                            {s.marketplaceName}
                            {s.skuName ? ` · ${s.skuName}` : ""}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.status || "-"}
                          size="small"
                          sx={{
                            backgroundColor: sc.bg,
                            color: sc.fg,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {s.noOfLicences ?? "-"}
                      </TableCell>
                      <TableCell align="right">
                        {breakdown && breakdown.length > 1 ? (
                          <Tooltip
                            arrow
                            title={
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 4,
                                  }}
                                >
                                  Tier breakdown
                                </div>
                                {breakdown}
                              </div>
                            }
                          >
                            <span
                              style={{
                                borderBottom: "1px dashed #6b7280",
                                cursor: "help",
                              }}
                            >
                              {perUnit}
                            </span>
                          </Tooltip>
                        ) : (
                          perUnit
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatUnit(totalAmount(s), s.currency)}
                      </TableCell>
                      <TableCell>
                        {formatInterval(s.n_interval, s.interval)}
                      </TableCell>
                      <TableCell>{formatDate(s.dueDate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActiveSubscriptionsBadge;
